import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { ProjectsLayout } from '../../layouts/projects-layout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Separator } from '../../components/ui/separator'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form'
import { UploadCloudIcon, TrashIcon } from 'lucide-react'

type ProfileForm = {
  first_name: string
  last_name: string
  password?: string
  password_confirmation?: string
}

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  avatar_url: string | null
  initials: string
  has_avatar: boolean
  is_sso_user: boolean
  has_password: boolean
  sso_provider: string | null
}

interface ProfileEditProps {
  user: User
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Edit({ user }: ProfileEditProps) {
  const { t } = useTranslation('profile')
  const avatarInputRef = useRef<HTMLInputElement | null>(null)

  const profileSchema = z.object({
    first_name: z.string().min(1, t('validation.firstNameRequired')),
    last_name: z.string().min(1, t('validation.lastNameRequired')),
    password: z.string().min(8, t('validation.passwordMinLength')).optional().or(z.literal('')),
    password_confirmation: z.string().optional(),
  }).refine((data) => {
    if (data.password && data.password.length > 0) {
      return data.password === data.password_confirmation
    }
    return true
  }, {
    message: t('validation.passwordsMustMatch'),
    path: ['password_confirmation'],
  })

  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user.avatar_url)
  const [removeAvatar, setRemoveAvatar] = useState(false)

  const form = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      password_confirmation: '',
    },
  })

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setAvatarFile(file)
      setRemoveAvatar(false)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveAvatar = () => {
    setAvatarFile(null)
    setAvatarPreview(null)
    setRemoveAvatar(true)
    if (avatarInputRef.current) avatarInputRef.current.value = ''
  }

  const onSubmit = (data: ProfileForm) => {
    const formData = new FormData()

    formData.append('user[first_name]', data.first_name)
    formData.append('user[last_name]', data.last_name)

    if (data.password && data.password.length > 0) {
      formData.append('user[password]', data.password)
      formData.append('user[password_confirmation]', data.password_confirmation || '')
    }

    if (avatarFile) {
      formData.append('user[avatar]', avatarFile)
    }

    if (removeAvatar) {
      formData.append('user[remove_avatar]', 'true')
    }

    router.patch('/profile', formData, {
      forceFormData: true,
      preserveScroll: true,
    })
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <section className="mx-auto max-w-7xl py-3">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                {/* Personal Information Section */}
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
                  <div className="flex flex-col space-y-1">
                    <h3 className="font-semibold">{t('sections.personalInfo')}</h3>
                    <p className="text-muted-foreground text-sm">{t('sections.personalInfoDesc')}</p>
                  </div>

                  <div className="space-y-6 lg:col-span-2">
                    {/* Avatar Upload */}
                    <div className="w-full space-y-2">
                      <Label>{t('photo.title')}</Label>
                      <div className="flex items-center gap-4">
                        <div
                          role="button"
                          tabIndex={0}
                          aria-label={t('photo.change')}
                          onClick={() => avatarInputRef.current?.click()}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              avatarInputRef.current?.click()
                            }
                          }}
                          className="flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-dashed hover:opacity-95"
                        >
                          {avatarPreview ? (
                            <img src={avatarPreview} alt="avatar preview" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-primary text-primary-foreground text-xl font-semibold">
                              {user.initials}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            ref={avatarInputRef}
                            type="file"
                            accept="image/png,image/jpg,image/jpeg,image/gif"
                            className="hidden"
                            onChange={handleAvatarChange}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => avatarInputRef.current?.click()}
                            className="flex items-center gap-2"
                          >
                            <UploadCloudIcon />
                            {t('photo.change')}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={handleRemoveAvatar}
                            disabled={!avatarPreview}
                            className="text-destructive"
                          >
                            <TrashIcon />
                          </Button>
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm">{t('photo.requirements')}</p>
                    </div>

                    {/* Name Fields - 2 column grid */}
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="first_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t('accountInfo.firstName')} <span className="text-destructive">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input type="text" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="last_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t('accountInfo.lastName')} <span className="text-destructive">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input type="text" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Email (Read-only) */}
                    <div className="flex flex-col items-start gap-2">
                      <Label>{t('accountInfo.email')}</Label>
                      <Input type="email" value={user.email} disabled />
                      <p className="text-muted-foreground text-sm">{t('accountInfo.emailCannotChange')}</p>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" disabled={form.formState.isSubmitting} className="max-sm:w-full">
                        {form.formState.isSubmitting ? t('actions.saving') : t('actions.save')}
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator className="my-10" />

                {/* Password Section */}
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
                  <div className="flex flex-col space-y-1">
                    <h3 className="font-semibold">
                      {user.is_sso_user && !user.has_password
                        ? t('password.addTitle')
                        : t('password.title')}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {user.is_sso_user && !user.has_password
                        ? t('password.addSubtitle')
                        : t('password.subtitle')}
                    </p>
                    {user.is_sso_user && user.sso_provider && (
                      <span className="inline-flex items-center self-start px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mt-2">
                        {user.sso_provider.charAt(0).toUpperCase() + user.sso_provider.slice(1)}
                      </span>
                    )}
                  </div>

                  <div className="space-y-6 lg:col-span-2">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('password.new')}</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder={t('password.newPlaceholder')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password_confirmation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('password.confirm')}</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder={t('password.confirmPlaceholder')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end">
                      <Button type="submit" disabled={form.formState.isSubmitting} className="max-sm:w-full">
                        {form.formState.isSubmitting ? t('actions.saving') : t('actions.save')}
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            </Form>
          </section>
        </div>
      </div>
    </div>
  )
}

Edit.layout = (page: React.ReactNode) => <ProjectsLayout>{page}</ProjectsLayout>

export default Edit
